import {
  ABILITIES,
  CLASSES,
  abilityDef,
  auraDef,
  type AbilityId,
  type AuraId,
  type ClassId,
  type CombatEvent,
} from '@arena/shared';

export interface MeterRow {
  /** An ability, an aura (ticks and shields), or `melee`; a spell's hit and its ticks share one. */
  id: string;
  name: string;
  icon: string;
  amount: number;
}

/** The player's own damage and healing by spell, from the events the server sends them. */
export class Meter {
  damage = new Map<string, number>();
  healing = new Map<string, number>();
  private first: number | null = null;
  private last = 0;

  add(events: CombatEvent[], me: string, now: number): void {
    for (const e of events) {
      if (!('source' in e) || e.source !== me) continue;
      if (e.type === 'damage')
        bump(this.damage, e.auraId ?? e.abilityId, e.amount + e.absorbed);
      else if (e.type === 'heal')
        bump(this.healing, e.auraId ?? e.abilityId, e.amount);
      else if (e.type === 'absorb') bump(this.healing, e.auraId, e.amount);
      else continue;
      this.first ??= now;
      this.last = now;
    }
  }

  /** From the first hit or heal to the latest, at least a second. */
  get seconds(): number {
    return this.first === null ? 1 : Math.max(1, this.last - this.first);
  }
}

function bump(totals: Map<string, number>, id: string | null, amount: number) {
  if (id) totals.set(id, (totals.get(id) ?? 0) + amount);
}

/** Largest first. */
export const meterRows = (
  totals: Map<string, number>,
  classId: ClassId,
): MeterRow[] =>
  [...totals]
    .map(([id, amount]) => ({ id, amount, ...spell(id, classId) }))
    .sort((a, b) => b.amount - a.amount);

function spell(id: string, classId: ClassId) {
  if (id === 'melee')
    return {
      name: CLASSES[classId].swing.range ? 'Auto Shot' : 'Auto Attack',
      icon: classId,
    };
  if (id in ABILITIES) {
    const ability = abilityDef(id as AbilityId);
    return { name: ability.name, icon: ability.icon ?? id };
  }
  const def = auraDef(id as AuraId);
  return { name: def.name, icon: def.icon ?? id };
}
